import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import type { ServiceFAQItem } from '../types';

interface Props {
  heading: string;
  subheading: string;
  faqs: ServiceFAQItem[];
}

function FAQItem({ q, a, open, onToggle }: { q: string; a: string; open: boolean; onToggle: () => void }) {
  return (
    <div
      className="border border-[#C8A96A]/15 rounded-xl overflow-hidden transition-all duration-300"
      style={{ background: open ? 'linear-gradient(135deg, #0D1E35, #0A1628)' : 'rgba(10,22,40,0.6)' }}
      itemScope
      itemProp="mainEntity"
      itemType="https://schema.org/Question"
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer"
        aria-expanded={open}
      >
        <span
          className="font-semibold text-sm md:text-base leading-snug transition-colors duration-200"
          style={{ color: open ? '#E0C78A' : 'rgba(255,255,255,0.85)' }}
          itemProp="name"
        >
          {q}
        </span>
        <div
          className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-full border border-[#C8A96A]/30 transition-all duration-300"
          style={{ background: open ? 'rgba(200,169,106,0.15)' : 'transparent' }}
        >
          <i
            className="ri-add-line text-[#C8A96A] text-base transition-transform duration-300"
            style={{ transform: open ? 'rotate(45deg)' : 'rotate(0deg)' }}
          />
        </div>
      </button>

      <div
        className="overflow-hidden transition-all duration-400 ease-in-out"
        style={{ maxHeight: open ? '350px' : '0px' }}
        itemScope
        itemProp="acceptedAnswer"
        itemType="https://schema.org/Answer"
      >
        <p className="px-6 pb-6 text-sm text-white/60 leading-relaxed" itemProp="text">
          {a}
        </p>
      </div>
    </div>
  );
}

export default function ServiceFAQ({ heading, subheading, faqs }: Props) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const { ref: headRef, isVisible: headVisible } = useScrollAnimation();

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section
      className="py-24 bg-[#0A1628] relative overflow-hidden"
      itemScope
      itemType="https://schema.org/FAQPage"
    >
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(200,169,106,0.04) 0%, transparent 70%)' }}
      />

      <div className="max-w-4xl mx-auto px-4 md:px-6 relative z-10">
        <div
          ref={headRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-14 transition-all duration-700 ${headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 border border-[#C8A96A]/25 rounded-full mb-5">
            <i className="ri-question-answer-line text-[#C8A96A] text-xs" />
            <span className="text-[#C8A96A] text-xs tracking-widest uppercase font-medium">FAQs</span>
          </div>
          <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {heading.split(' ').slice(0, -1).join(' ')}{' '}
            <span className="bg-gradient-to-r from-[#C8A96A] to-[#E0C78A] bg-clip-text text-transparent">
              {heading.split(' ').slice(-1)[0]}
            </span>
          </h2>
          <p className="text-white/50 text-sm md:text-base max-w-xl mx-auto">{subheading}</p>
        </div>

        <div className="flex flex-col gap-3">
          {faqs.map((item, i) => (
            <FAQItem
              key={i}
              q={item.q}
              a={item.a}
              open={openIndex === i}
              onToggle={() => toggle(i)}
            />
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-white/40 text-sm mb-4">Still have a question? We&apos;re happy to help.</p>
          <a
            href="tel:01529265060"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#C8A96A] to-[#A8894F] hover:from-[#E0C78A] hover:to-[#C8A96A] text-black font-bold px-8 py-3.5 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap text-sm"
          >
            <i className="ri-phone-fill" />
            Call 01529 265060
          </a>
        </div>
      </div>
    </section>
  );
}
